import React, { useCallback, useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { RefreshCw, Trash2, AlertTriangle, Info, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["", "ERROR", "WARNING", "INFO", "DEBUG"];

const LEVEL_META = {
  ERROR:   { text: "text-red-400",    border: "border-red-800",   dot: "bg-red-500",   msgColor: "text-red-300"    },
  WARNING: { text: "text-amber-400",  border: "border-amber-700", dot: "bg-amber-400", msgColor: "text-amber-200"  },
  INFO:    { text: "text-blue-400",   border: "border-blue-800",  dot: "bg-blue-400",  msgColor: "text-blue-200"   },
  DEBUG:   { text: "text-zinc-500",   border: "border-zinc-700",  dot: "bg-zinc-600",  msgColor: "text-zinc-400"   },
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR") + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function LogRow({ log }) {
  const meta = LEVEL_META[log.level] || LEVEL_META.DEBUG;

  return (
    <tr
      data-testid={`log-row-${log.id}`}
      className="border-b border-zinc-800 hover:bg-zinc-800/40 transition-colors"
    >
      <td className="px-4 py-2 text-[10px] font-mono text-zinc-500 whitespace-nowrap align-top">
        {formatDate(log.created_at)}
      </td>
      <td className="px-3 py-2 whitespace-nowrap align-top">
        <span className={`inline-flex items-center gap-1.5 text-[9px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 border ${meta.text} ${meta.border} bg-transparent`}>
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
          {log.level}
        </span>
      </td>
      <td className="px-3 py-2 text-[10px] font-mono text-zinc-500 whitespace-nowrap max-w-[160px] truncate align-top">
        {log.module || "—"}
      </td>
      <td className="px-3 py-2 align-top">
        <div className={`text-[10px] font-mono whitespace-pre-wrap break-all leading-relaxed ${meta.msgColor}`}>
          {log.message}
        </div>
      </td>
    </tr>
  );
}

export default function ServerLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [level, setLevel] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/logs", {
        params: { level: level || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [level, page]);

  useEffect(() => { setPage(0); }, [level]);
  useEffect(() => { load(); }, [load]);

  const handleClear = async () => {
    if (!window.confirm("Supprimer tous les logs serveur ?")) return;
    try {
      await api.delete("/super-admin/logs");
      toast.success("Logs effacés.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const errorCount   = logs.filter((l) => l.level === "ERROR").length;
  const warningCount = logs.filter((l) => l.level === "WARNING").length;

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Logs serveur</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {total.toLocaleString("fr-FR")} entrée{total > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="logs-refresh-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 border border-zinc-300 text-zinc-700 text-xs px-3 py-2 hover:bg-zinc-50 disabled:opacity-50 font-mono"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>
          <button
            data-testid="logs-clear-btn"
            onClick={handleClear}
            className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 text-xs px-3 py-2 hover:bg-red-50 font-mono"
          >
            <Trash2 size={12} />
            Effacer
          </button>
        </div>
      </div>

      {/* Summary chips */}
      {(errorCount > 0 || warningCount > 0) && (
        <div className="flex gap-3 text-xs font-mono">
          {errorCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 border border-red-200 bg-red-50 text-red-700">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              {errorCount} erreur{errorCount > 1 ? "s" : ""}
            </span>
          )}
          {warningCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 border border-amber-200 bg-amber-50 text-amber-700">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              {warningCount} avertissement{warningCount > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <span className="text-[9px] uppercase tracking-[0.2em] font-mono text-zinc-400">Niveau</span>
        <div className="flex gap-1.5">
          {LEVELS.map((l) => (
            <button
              key={l || "ALL"}
              data-testid={`log-filter-${l || "all"}`}
              onClick={() => setLevel(l)}
              className={`text-[10px] font-mono px-3 py-1 border transition-colors ${
                level === l
                  ? "border-zinc-900 bg-zinc-900 text-white"
                  : "border-zinc-200 text-zinc-600 hover:border-zinc-400"
              }`}
            >
              {l || "Tous"}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border border-zinc-800 bg-zinc-950 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="logs-table">
            <thead>
              <tr className="border-b border-zinc-700 bg-zinc-800/60">
                <th className="px-4 py-2.5 text-left text-[9px] uppercase tracking-[0.2em] font-mono text-zinc-500 whitespace-nowrap">Date / Heure</th>
                <th className="px-3 py-2.5 text-left text-[9px] uppercase tracking-[0.2em] font-mono text-zinc-500">Niveau</th>
                <th className="px-3 py-2.5 text-left text-[9px] uppercase tracking-[0.2em] font-mono text-zinc-500">Module</th>
                <th className="px-3 py-2.5 text-left text-[9px] uppercase tracking-[0.2em] font-mono text-zinc-500">Message</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-xs text-zinc-600 font-mono">Chargement…</td></tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <AlertCircle size={24} className="mx-auto text-zinc-700 mb-2" strokeWidth={1} />
                    <div className="text-xs text-zinc-600 font-mono">Aucun log{level ? ` de niveau ${level}` : ""}</div>
                  </td>
                </tr>
              ) : logs.map((log) => <LogRow key={log.id} log={log} />)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800 bg-zinc-900 text-[10px] font-mono text-zinc-500">
            <span>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} sur {total.toLocaleString("fr-FR")}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 border border-zinc-700 disabled:opacity-40 hover:bg-zinc-800"
              >
                ← Précédent
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total}
                className="px-3 py-1 border border-zinc-700 disabled:opacity-40 hover:bg-zinc-800"
              >
                Suivant →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

